import { TrackEventParams } from '@jonkoops/matomo-tracker/lib/types'
import {
    bulkEnrollmentExitStepFromPathname,
    BulkEnrollmentExitStep,
    ensurePositiveDuration,
    getBulkEnrollmentCurrentStep,
    getBulkEnrollmentRowDurationMs,
    getBulkEnrollmentSessionDurationMs,
    hasActiveBulkEnrollmentSession,
    isBulkEnrollmentSessionClosed,
    markBulkEnrollmentSessionAbandoned,
    markBulkEnrollmentSessionCompleted,
    markBulkEnrollmentStartedTracked,
    resetBulkEnrollmentAnalyticsSession,
    startBulkEnrollmentRowTimer,
    wasBulkEnrollmentStartedTracked,
    withBulkEnrollmentConnectivity
} from 'helpers/bulkEnrollmentAnalytics'
import { constants } from 'helpers/constants'
import { getConnectUser } from 'helpers'
import { useAnalytics } from 'hooks/useAnalytics'

const bulkEnrollmentCategory = constants.ANALYTICS.CATEGORIES.BULK_ENROLLMENT
const { ACTIONS, NAMES } = constants.ANALYTICS

const villageCodeFor = (villageId?: string) => {
    const { veVillages = [] } = getConnectUser()
    const match = veVillages.find(
        (village: { sf_id?: string; code?: string }) =>
            village.sf_id === villageId
    )
    return match?.code || villageId || ''
}

const isOnline = () =>
    typeof navigator === 'undefined' || navigator.onLine

const connectivityName = (name: string) =>
    withBulkEnrollmentConnectivity(name, isOnline())

export const useBulkEnrollmentAnalytics = (villageId?: string) => {
    const { trackEvent: trackMatomoEvent } = useAnalytics()

    const trackEnrollmentEvent = (
        action: string,
        extras: Partial<TrackEventParams> = {}
    ) => {
        trackMatomoEvent({
            category: bulkEnrollmentCategory,
            action,
            ...extras
        })
    }

    const trackStarted = () => {
        if (hasActiveBulkEnrollmentSession() && wasBulkEnrollmentStartedTracked()) {
            return
        }
        if (isBulkEnrollmentSessionClosed()) {
            resetBulkEnrollmentAnalyticsSession()
        }
        markBulkEnrollmentStartedTracked()
        trackEnrollmentEvent(ACTIONS.BULK_ENROLLMENT_STARTED, {
            name: villageCodeFor(villageId)
        })
    }

    const trackCompleted = (rowCount: number) => {
        if (!hasActiveBulkEnrollmentSession()) {
            return
        }
        const value = getBulkEnrollmentSessionDurationMs()
        markBulkEnrollmentSessionCompleted()
        trackEnrollmentEvent(ACTIONS.BULK_ENROLLMENT_COMPLETED, {
            name: String(rowCount),
            value
        })
    }

    const trackAbandoned = (step?: BulkEnrollmentExitStep) => {
        if (!hasActiveBulkEnrollmentSession()) {
            return
        }
        const exitStep =
            step ||
            getBulkEnrollmentCurrentStep() ||
            bulkEnrollmentExitStepFromPathname(window.location.pathname)
        const value = getBulkEnrollmentSessionDurationMs()
        markBulkEnrollmentSessionAbandoned()
        trackEnrollmentEvent(ACTIONS.BULK_ENROLLMENT_ABANDONED, {
            name: `${NAMES.BULK_EXIT}-${exitStep}`,
            value
        })
    }

    const startRowTimer = (rowId: string) => {
        startBulkEnrollmentRowTimer(rowId)
    }

    const trackRowConfirmed = (
        rowId: string,
        clientType: 'new_client' | 'existing_client'
    ) => {
        trackEnrollmentEvent(ACTIONS.BULK_ROW_CONFIRMED, {
            name: clientType,
            value: getBulkEnrollmentRowDurationMs(rowId)
        })
    }

    const trackNameCorrected = (
        clientType: 'new_client' | 'existing_client'
    ) => {
        trackEnrollmentEvent(ACTIONS.BULK_NAME_CORRECTED, {
            name: clientType
        })
    }

    const trackRowRemoved = () => {
        trackEnrollmentEvent(ACTIONS.BULK_ROW_REMOVED)
    }

    const trackValidationError = (
        errorName: 'invalid_number' | 'agent_number' | 'duplicate'
    ) => {
        trackEnrollmentEvent(ACTIONS.BULK_ROW_VALIDATION_ERROR, {
            name: errorName
        })
    }

    const trackPackageQuantityChanged = (
        quantity: number,
        direction: 'increase' | 'decrease'
    ) => {
        trackEnrollmentEvent(ACTIONS.BULK_PACKAGE_QUANTITY_CHANGED, {
            name: direction,
            value: quantity
        })
    }

    const trackBatchSubmitted = (durationMs: number, rowCount: number) => {
        trackEnrollmentEvent(ACTIONS.BULK_BATCH_SUBMITTED, {
            name: connectivityName(String(rowCount)),
            value: ensurePositiveDuration(durationMs)
        })
    }

    return {
        trackStarted,
        trackCompleted,
        trackAbandoned,
        startRowTimer,
        trackRowConfirmed,
        trackNameCorrected,
        trackRowRemoved,
        trackValidationError,
        trackPackageQuantityChanged,
        trackBatchSubmitted
    }
}
